import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PendingEvidenciaField } from "@/components/admin/portal-tickets/tickets/TicketEvidencia";
import type { PendingAdjunto } from "@/lib/portal-tickets/tickets-adjuntos";
import { enviarCorreoAsignacion } from "@/lib/portal-tickets/tickets-store";
import {
  ETIQUETA_CAMPO,
  PIPELINE_SOPORTE_DATOS,
  correosANotificar,
  solicitarCambioDatos,
  subirEvidencias,
  type CambioDatosCancelado,
  type CambiosDatos,
  type CampoConAutorizacion,
  type ResultadoSolicitud,
} from "@/lib/usuarios/cambio-datos-comprador";

const MIN_MOTIVO = 10;

interface Props {
  persona: { id: number; nombre: string };
  cambios: CambiosDatos;
  vista: string;
  /** Quién pide, para el correo al asignado. */
  solicitante?: string | null;
  onEnviada: (r: ResultadoSolicitud) => void;
  onCancelar: (motivo?: CambioDatosCancelado) => void;
}

/**
 * Captura motivo + evidencia de un cambio de nombre/correo/teléfono de comprador y lo manda
 * a autorización. No cambia ningún dato: eso lo hace el RPC al aprobarlo quien tenga el permiso.
 */
export function SolicitudCambioDatosDialog({ persona, cambios, vista, solicitante, onEnviada, onCancelar }: Props) {
  const [motivo, setMotivo] = useState("");
  const [evidencias, setEvidencias] = useState<PendingAdjunto[]>([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => () => evidencias.forEach((e) => URL.revokeObjectURL(e.previewUrl)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const motivoOk = motivo.trim().length >= MIN_MOTIVO;
  const puedeEnviar = motivoOk && evidencias.length > 0 && !enviando;

  const enviar = async () => {
    if (!puedeEnviar) return;
    setEnviando(true);
    try {
      const subidas = await subirEvidencias(evidencias);
      const r = await solicitarCambioDatos({ idPersona: persona.id, cambios, motivo, evidencias: subidas, vista });
      // Aviso a los propietarios del ticket: la categoría "Aprobador" del pipeline
      // (fire-and-forget, como los tickets del portal).
      const correos = correosANotificar(r);
      if (!correos.length) {
        toast.warning("La solicitud quedó registrada, pero nadie recibió el aviso", {
          description: `Falta configurar la categoría "Aprobador" en el pipeline ${PIPELINE_SOPORTE_DATOS}.`,
        });
      }
      enviarCorreoAsignacion(correos.map((email) => ({ email })), {
        folio: r.numero_ticket,
        nombre: `Cambio de datos: ${persona.nombre}`,
        pipeline: PIPELINE_SOPORTE_DATOS,
        descripcion: `${resumen(cambios)}\n\nMotivo: ${motivo.trim()}`,
        por: solicitante ?? null,
      });
      onEnviada(r);
    } catch (e: any) {
      toast.error(`No se envió la solicitud: ${e?.message ?? e}`);
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !enviando && onCancelar()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Enviar cambio a revisión y aprobación
          </DialogTitle>
          <DialogDescription>
            El nombre, el correo y el teléfono de <strong>{persona.nombre}</strong> no se cambian directo. Se levanta
            un ticket en <strong>{PIPELINE_SOPORTE_DATOS}</strong> y el cambio se aplica solo cuando lo aprueba un Super
            Administrador. Los demás datos del formulario se guardan normal.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Dato</th>
                <th className="px-3 py-2 font-medium">Valor actual</th>
                <th className="px-3 py-2 font-medium">Valor nuevo</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(cambios) as CampoConAutorizacion[]).map((campo) => (
                <tr key={campo} className="border-t">
                  <td className="px-3 py-2 font-medium">{ETIQUETA_CAMPO[campo]}</td>
                  <td className="px-3 py-2 text-muted-foreground line-through decoration-muted-foreground/50">
                    {cambios[campo]?.actual || "(vacío)"}
                  </td>
                  <td className="px-3 py-2 font-medium text-foreground">{cambios[campo]?.nuevo || "(vacío)"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-2">
          <Label htmlFor="motivo-cambio">Motivo del cambio *</Label>
          <Textarea
            id="motivo-cambio"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. El cliente pidió corregir su correo por llamada del 23/09; adjunto la grabación."
            rows={3}
            disabled={enviando}
          />
          <p className={`text-xs ${motivoOk || !motivo ? "text-muted-foreground" : "text-destructive"}`}>
            Mínimo {MIN_MOTIVO} caracteres.
          </p>
        </div>

        <div className="space-y-1">
          <PendingEvidenciaField
            value={evidencias}
            onChange={setEvidencias}
            disabled={enviando}
            label="Evidencia * (imagen, video, audio o documento)"
          />
          {evidencias.length === 0 && (
            <p className="text-xs text-muted-foreground">Adjunta al menos una evidencia que respalde el cambio.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onCancelar()} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!puedeEnviar}>
            {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {enviando ? "Enviando…" : "Enviar a revisión"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function resumen(cambios: CambiosDatos): string {
  return (Object.keys(cambios) as CampoConAutorizacion[])
    .map((c) => `${ETIQUETA_CAMPO[c]}: "${cambios[c]?.actual || "(vacío)"}" → "${cambios[c]?.nuevo || "(vacío)"}"`)
    .join("\n");
}
